import { BackupUploadingStatus } from '../../contracts';

/** Mirrors `PersistedStatus` in `@heidi/capture` (persistenceV2/types.ts). */
export type PersistedAudioStatus = 'pending' | 'uploaded' | 'failed' | 'abandoned';

/** `unknown`: the ledger read has not settled. `unreadable`: it failed. */
type PersistedStatusesRead = readonly PersistedAudioStatus[] | 'unknown' | 'unreadable';

export type ClientTranscriptGuardInput = {
  liveChunkUploadInProgress: boolean;
  inFlightChunkUploads: boolean;
  /** Chunks this page holds for retry; the ledger may have no row for them. */
  retainedChunks: boolean;
  persistedChunkStatuses: PersistedStatusesRead;
  persistedBackupStatuses: PersistedStatusesRead;
  recordingTimerRunning: boolean;
  backupUploadingStatus: BackupUploadingStatus | undefined;
  /** `in_progress`: the stop IPC is still finalizing the last chunk and backup. */
  rustStop:
    | { failedChunks: number; backupUploaded: boolean; backupPendingAsync: boolean }
    | 'in_progress'
    | null;
  transcriptDeleteInProgress: boolean;
  transcriptDeleted: boolean;
};

/** What this device still holds for one session that the backend cannot see. */
export type ClientTranscriptDeviceSignals = Pick<
  ClientTranscriptGuardInput,
  | 'liveChunkUploadInProgress'
  | 'inFlightChunkUploads'
  | 'retainedChunks'
  | 'persistedChunkStatuses'
  | 'persistedBackupStatuses'
  | 'recordingTimerRunning'
>;

export const CLEAR_DEVICE_SIGNALS: ClientTranscriptDeviceSignals = {
  liveChunkUploadInProgress: false,
  inFlightChunkUploads: false,
  retainedChunks: false,
  persistedChunkStatuses: [],
  persistedBackupStatuses: [],
  recordingTimerRunning: false,
};

/** Why the claim is hidden; a closed set. */
export type ClientTranscriptGuardReason =
  | 'transcript_deleted'
  | 'transcript_delete_in_progress'
  | 'recording_timer'
  | 'live_chunk_upload'
  | 'in_flight_chunk_upload'
  | 'rust_stop_in_progress'
  | 'rust_stop_failed_chunks'
  | 'rust_stop_backup_unconfirmed'
  | 'backup_status'
  | 'ledger_chunk_not_uploaded'
  | 'ledger_backup_not_uploaded'
  | 'ledger_unreadable'
  | 'ledger_unread'
  | 'retained_chunk_not_uploaded';

const BACKUP_STATUS_HIDES: Record<BackupUploadingStatus, boolean> = {
  [BackupUploadingStatus.NOT_STARTED]: false,
  [BackupUploadingStatus.PENDING]: true,
  [BackupUploadingStatus.UPLOADING]: true,
  [BackupUploadingStatus.SUCCESS]: false,
  [BackupUploadingStatus.FAILED]: true,
};

/**
 * Why this session's "transcript is safe" claim must stay hidden, or null: this
 * device still has recording work the backend cannot see, or the transcript is
 * deleted or being deleted. It does not decide that a transcript exists.
 */
export function resolveClientTranscriptGuard(
  input: ClientTranscriptGuardInput
): ClientTranscriptGuardReason | null {
  const {
    rustStop,
    persistedChunkStatuses: chunks,
    persistedBackupStatuses: backups,
  } = input;
  if (input.transcriptDeleted) return 'transcript_deleted';
  if (input.transcriptDeleteInProgress) return 'transcript_delete_in_progress';
  if (input.recordingTimerRunning) return 'recording_timer';
  if (input.liveChunkUploadInProgress) return 'live_chunk_upload';
  if (input.inFlightChunkUploads) return 'in_flight_chunk_upload';
  if (rustStop === 'in_progress') return 'rust_stop_in_progress';
  if (rustStop !== null && rustStop.failedChunks > 0) return 'rust_stop_failed_chunks';
  if (rustStop !== null && !rustStop.backupUploaded && !rustStop.backupPendingAsync) {
    return 'rust_stop_backup_unconfirmed';
  }
  if (input.backupUploadingStatus && BACKUP_STATUS_HIDES[input.backupUploadingStatus]) {
    return 'backup_status';
  }
  if (chunks === 'unreadable' || backups === 'unreadable') return 'ledger_unreadable';
  if (chunks === 'unknown' || backups === 'unknown') return 'ledger_unread';
  if (chunks.some((status) => status !== 'uploaded')) return 'ledger_chunk_not_uploaded';
  if (backups.some((status) => status !== 'uploaded'))
    return 'ledger_backup_not_uploaded';
  if (input.retainedChunks) return 'retained_chunk_not_uploaded';
  return null;
}
